/**
 * Shared types for the Bulk Assign Units drawer.
 *
 * Vocabulary note: the design calls a parent group a "production" and a child
 * group a "site". In the database both are rows in `groups` — a production has
 * `parent_group_id = null`, a site points at its production. A unit lands on
 * exactly one group: the site when it has one, otherwise the production.
 */

/** Where a staged unit is headed. `PARENT_GROUP_TARGET` means "no site". */
export const PARENT_GROUP_TARGET = 'prod';

export interface BulkAssignUnit {
  id: string;
  unitCode: string;
  modelCode: string;
  companyId: string;
  companyName: string;
  /** The group the unit is on today (site or production), null when unassigned. */
  primaryGroupId: string | null;
  /** Resolved production for the current assignment. */
  parentGroupId: string | null;
  /** Display name of the current production, null when unassigned. */
  parentGroupName: string | null;
  /** Resolved site for the current assignment, null when it sits on the production. */
  siteId: string | null;
  status: string;
}

/** A production (parentGroupId === null) or a site (parentGroupId set). */
export interface BulkAssignGroup {
  id: string;
  name: string;
  companyId: string;
  parentGroupId: string | null;
}

export interface BulkAssignCompany {
  id: string;
  name: string;
}

export interface DateWindow {
  start: string;
  end: string;
}

/** A pasted code that matched no unit the user can see. */
export interface UnmatchedCode {
  code: string;
  /** `ambiguous`: shorthand (`MED1`) that matches more than one unit; the user picks. */
  kind: 'typo' | 'missing' | 'ambiguous';
  /** Present when `kind === 'typo'` — the nearest real unit code. */
  suggestion: string | null;
  /** Present when `kind === 'ambiguous'` — every unit the shorthand matches. */
  candidates?: string[];
}

export type AssignOutcome = 'assigned' | 'moved' | 'unchanged' | 'failed';

export interface AssignmentResult {
  unitId: string;
  unitCode: string;
  outcome: AssignOutcome;
  fromGroupName: string | null;
  toGroupName: string;
  /** Destination group id — names are not unique, so colour/lookup uses this. */
  toGroupId?: string;
  isSite: boolean;
  startDate: string | null;
  endDate: string | null;
  /** Previous status when this assignment activated the unit. */
  activatedFrom: string | null;
}

export interface BulkAssignResponse {
  assigned: number;
  unchanged: number;
  /** Entries whose write failed — assigned + unchanged + failed === results.length. */
  failed: number;
  results: AssignmentResult[];
  warnings: string[];
}

/** One row of the request sent to POST /api/v1/admin/units/bulk-assign. */
export interface BulkAssignRequestRow {
  unitId: string;
  groupId: string;
  startDate: string | null;
  endDate: string | null;
}
